// The sky is a score — the sound layer. Ported from the playable study
// (docs/prototype/quiet-skies-sound-v1.html; concept, mapping and tuning log
// in docs/research/music.md). Web Audio only: sine and triangle partials with
// per-partial decays, pink noise, a synthesised convolution reverb, one soft
// compressor. No audio assets, no libraries.
//
// Three layers, in the order the player meets them:
//  - the bed (drone, breathing pad on a chord cycle, wind, pulse, sparks, and
//    the conductor's slow arc over tens of minutes) plays whenever a level is
//    live and fades out under every veil;
//  - the ink (a filtered breath whose brightness follows draw speed, a faint
//    tone that follows the ring under the finger, a plink at each new ring)
//    plays while a route is being drawn;
//  - the aircraft: every routed aircraft plays its own line, striking the
//    ring it is nearest to every `tick` px of travel in a timbre that belongs
//    to its type; an unrouted one asks a question every few bars; landing is
//    tonic plus fifth and the pad takes a breath; a crash ducks the bed.
// Everything an aircraft or the finger triggers is quantised to the next
// eighth of the city's tempo, so five aircraft at five speeds fall into a
// polyrhythm instead of a scatter. Nothing is an alarm.
//
// Kept deliberately out of src/sim: `observe(world)` reads the world after
// step() from the outside and never feeds anything back; the layer is
// cosmetic and non-deterministic (wall-clock envelopes, its own RNG), like
// the renderer's ripples. The rules it plays by — scales, chords, phases,
// drift — are src/app/music.ts, testable without a browser.
//
// iOS rule: an AudioContext may only be created/resumed inside a user
// gesture. `unlock()` is called from document-level pointerdown/touchend/
// click listeners in main.ts (capture phase, so it always runs even if a
// target — e.g. the mute toggle — later calls stopPropagation on the same
// event). Every entry point is wrapped in try/catch and feature-detected so
// a missing or throwing AudioContext (Playwright WebKit, old browsers, a
// locked-down embed) degrades to a silent no-op rather than breaking play.
import type { Aircraft, ChordCycle, DroneRegister, Layout, LevelDef, MusicConfig, PadVoice, PadVoicing, Rng, ScaleName, World } from '../sim/index.ts';
import { SIM_DT, airborneCount, makeRng, nearestThreshold } from '../sim/index.ts';
import type { Chord, Phase, PhaseMul } from './music.ts';
import {
  DRAWBAR_IDX, DRAWBAR_PERIODS, MOTIF_PATTERNS, NO_MUL, PADS, PHASES, PROGS, REG, SCALES, VOICES, VOICING,
  degreeSemis, driftAway, fieldDegree, mtof, resolveMusic,
} from './music.ts';

const STORAGE_KEY = 'qs.sound.v1';
/** where the study's volume slider sat while every city was tuned by ear */
const MASTER_LEVEL = 0.8;
const TAU = Math.PI * 2;

/** how a drawn route ended — what the finger lifting should sound like */
export type InkResult = 'runway' | 'pad' | 'free' | 'hold' | 'cancel';

export interface SoundStats { ctx: string; beats: number; notes: number; phase: string | null; key: string; bed: number; master: number }
const NOTE_NAMES = ['c', 'c#', 'd', 'd#', 'e', 'f', 'f#', 'g', 'g#', 'a', 'a#', 'b'];

function loadMuted(): boolean {
  try { return localStorage.getItem(STORAGE_KEY) === 'off'; } catch { return false; }
}
function saveMuted(muted: boolean): void {
  try { localStorage.setItem(STORAGE_KEY, muted ? 'off' : 'on'); } catch { /* private mode — just won't persist */ }
}

type AudioContextCtor = typeof AudioContext;
function getAudioContextCtor(): AudioContextCtor | null {
  const w = window as unknown as { AudioContext?: AudioContextCtor; webkitAudioContext?: AudioContextCtor };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

function gainNode(ctx: AudioContext, v: number): GainNode {
  const g = ctx.createGain();
  g.gain.value = v;
  return g;
}

/** a synthesised room: lowpassed noise under an exponential tail of `seconds` */
function makeIR(ctx: AudioContext, seconds: number): AudioBuffer {
  const rate = ctx.sampleRate, len = Math.floor(rate * seconds), k = 6.9 / seconds;
  const buf = ctx.createBuffer(2, len, rate);
  for (let c = 0; c < 2; c++) {
    const d = buf.getChannelData(c);
    let lp = 0;
    for (let i = 0; i < len; i++) {
      const n = Math.random() * 2 - 1;
      lp += (n - lp) * 0.22;
      d[i] = lp * Math.exp(-k * i / rate);
    }
  }
  return buf;
}

/** pink-ish noise, Paul Kellet's three-pole approximation */
function makeNoise(ctx: AudioContext, seconds: number): AudioBuffer {
  const len = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);
  let b0 = 0, b1 = 0, b2 = 0;
  for (let i = 0; i < len; i++) {
    const w = Math.random() * 2 - 1;
    b0 = 0.99765 * b0 + w * 0.0990460;
    b1 = 0.96300 * b1 + w * 0.2965164;
    b2 = 0.57000 * b2 + w * 1.0526913;
    d[i] = (b0 + b1 + b2 + w * 0.1848) * 0.11;
  }
  return buf;
}

/** One pad instrument: three chord voices of one `PadVoice` into a mix node. Each voice lists
 *  the AudioParams that follow its chord tone, with their partial ratio, so any instrument is
 *  retuned the same way. The drift nodes are the conductor's "drawbar drift": slow LFOs so the
 *  timbre is never the same twice, at a depth `applyDrawbarDepths` sets (0 while the journey is off). */
interface PadInstrument {
  type: PadVoice;
  mix: GainNode;
  voices: { tuned: { p: AudioParam; ratio: number }[] }[];
  sources: AudioScheduledSourceNode[];
  filters: BiquadFilterNode[];
  drawbarDepths: { node: GainNode; amp: number }[];
  filterDrift2s: GainNode[];
  qDrifts: GainNode[];
}

/**
 * The audio graph, built once on unlock:
 *
 *   aircraft voices ─▶ planeBus ─▶ planeLP (lowpass, per-city brightness) ─┬─▶ master ─▶ compressor ─▶ out
 *                                                                          └─▶ reverb ─▶ verbGain ─▶ master
 *   drone, pad, wind, tense, pulse, sparks, motif ─▶ bedBus ─┬─▶ master
 *                                                            └─▶ bedSend (0.5) ─▶ reverb
 *   ink breath, preview tone, plinks ─▶ inkBus ─▶ master (+ reverb)
 */
interface Graph {
  master: GainNode;
  verb: ConvolverNode; verbGain: GainNode;
  planeBus: GainNode; planeLP: BiquadFilterNode;
  bedBus: GainNode; inkBus: GainNode;
  noise: AudioBuffer;
  droneGain: GainNode; droneLP: BiquadFilterNode; droneSwell: GainNode; droneLFOG: GainNode; droneOsc: OscillatorNode[];
  padGain: GainNode; padLFOG: GainNode; pad: PadInstrument;
  windGain: GainNode; windBP: BiquadFilterNode;
  tenseGain: GainNode; tenseOsc: OscillatorNode;
  inkGain: GainNode; inkBP: BiquadFilterNode; prevOsc: OscillatorNode; prevGain: GainNode;
}

/** the pad's discrete choices — the tuned ones, or a phase's while the journey is full */
interface Discrete { droneReg: DroneRegister; padVoicing: PadVoicing; padProg: ChordCycle }
interface Conductor { idx: number; phase: Phase; nextAt: number }
/** where an aircraft is along its line, kept on the sound side (never on the sim's aircraft) */
interface PlaneState { travel: number; lastDeg: number; nextCall: number }

export class SoundEngine {
  private ctx: AudioContext | null = null;
  private g: Graph | null = null;
  private muted: boolean;
  private bedWanted = false;
  private timer: ReturnType<typeof setInterval> | null = null;
  private irTimer: ReturnType<typeof setTimeout> | null = null;
  private irSeconds = 0;

  // the city: meaningful before any audio exists, applied to the graph once there is one
  private cfg: MusicConfig = resolveMusic({});
  private layout: Layout | null = null;
  private rng: Rng = makeRng(20260904);

  // harmony, and the conductor's state on top of the tuned settings
  private scale: readonly number[] = SCALES[this.cfg.mode];
  private driftedMode: ScaleName = this.cfg.mode;
  private chordIdx = 0;
  private active: Discrete = { droneReg: this.cfg.droneReg, padVoicing: this.cfg.padVoicing, padProg: this.cfg.padProg };
  private mul: PhaseMul = NO_MUL;
  private cond: Conductor | null = null;
  private driftOffset = 0;
  private driftIsAway = false;
  private phasesSinceDrift = 0;
  private driftPending = false;
  private motifPattern: readonly number[] = MOTIF_PATTERNS[0];
  private motifStep = 0;

  // the beat scheduler
  private nextBeat = 0;
  private beatIdx = 0;
  private droneResting = false;

  // the sky as last observed, and each aircraft's place on its line
  private airborne = -1;
  private warn = false;
  private readonly planes = new WeakMap<Aircraft, PlaneState>();
  // the finger
  private inkDeg = -1;
  private inkNoteAt = 0;
  /** every note struck so far — a smoke check that the graph plays, nothing else reads it */
  private notes = 0;

  constructor() {
    this.muted = loadMuted();
  }

  get isMuted(): boolean { return this.muted; }

  /** A read-only glance for tests and the console (`window.__qs.sound`): whether the context
   *  runs, the beat count, notes struck, the conductor's phase, the key now sounding, and the
   *  bed and master levels. Never used by the game itself. */
  get stats(): SoundStats {
    const g = this.g;
    return {
      ctx: this.ctx ? this.ctx.state : 'none',
      beats: this.beatIdx,
      notes: this.notes,
      phase: this.cond ? this.cond.phase.name : null,
      key: `${NOTE_NAMES[((this.effRoot() % 12) + 12) % 12]} ${this.driftedMode}${this.driftOffset ? ` ${this.driftOffset > 0 ? '+' : ''}${this.driftOffset}` : ''}`,
      bed: g ? g.bedBus.gain.value : 0,
      master: g ? g.master.gain.value : 0,
    };
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    saveMuted(muted);
    this.applyMasterGain();
  }

  toggleMuted(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  /** Create the AudioContext (once) on a user gesture and resume it if the
   *  browser suspended it — call from every pointerdown/touchend/click. */
  unlock(): void {
    try {
      if (!this.ctx) {
        const Ctor = getAudioContextCtor();
        if (!Ctor) return; // no Web Audio support — everything else stays a no-op
        const ctx = new Ctor({ latencyHint: 'interactive' });
        this.ctx = ctx;
        this.g = this.buildGraph(ctx);
        const t = ctx.currentTime + 0.05;
        this.applyChord(this.chordIdx, true);
        this.applyWind(t, 0.5);
        this.g.droneGain.gain.setTargetAtTime(this.cfg.drone * this.mul.drone, t, 2.5);
        this.g.padGain.gain.setTargetAtTime(this.cfg.pad * this.mul.pad, t + 1, 3);
        if (this.bedWanted) this.rampBed(1, 2);
        this.nextBeat = ctx.currentTime + 0.1;
        this.beatIdx = 0;
        this.timer = setInterval(() => this.schedule(), 25);
      }
      if (this.ctx.state === 'suspended') void this.ctx.resume().catch(() => {});
    } catch { /* AudioContext exists but construction/resume threw — stay silent */ }
  }

  /** Suspend while the tab is hidden (saves battery, avoids a stuck drone);
   *  resume when it's visible again, unless muted. */
  onVisibility(hidden: boolean): void {
    if (!this.ctx) return;
    try {
      if (hidden) void this.ctx.suspend().catch(() => {});
      else if (!this.muted && this.ctx.state === 'suspended') void this.ctx.resume().catch(() => {});
    } catch { /* no-op */ }
  }

  /** A city begins: its key, tempo and room, and a fresh hour for the conductor. Call at
   *  level start, before `bedIn()`; retuning glides, it never restarts the graph. */
  setLevel(level: LevelDef): void {
    this.cfg = resolveMusic(level);
    this.rng = makeRng((Date.now() ^ (level.id.length << 24)) >>> 0);
    this.resetArc();
    this.chordIdx = 0;
    this.inkDeg = -1;
    this.applyAll();
  }

  /** The bed fades in over ~2 s — a level is live (start, resume, fly on). */
  bedIn(): void {
    this.bedWanted = true;
    this.rampBed(1, 2);
  }

  /** The bed fades out over ~1 s — a veil is up (intro, pause). A crash ducks
   *  it its own way and a clear keeps it playing under the veil. */
  bedOut(): void {
    this.bedWanted = false;
    this.rampBed(0, 1);
  }

  /**
   * Read the world right after a sim step: its events, how many aircraft are
   * up and whether two are inside the warning band (wind and tension), and
   * every aircraft's travel along its line (the notes) or its silence (the
   * calls). The aircraft's own record is a WeakMap here, never a field on it.
   */
  observe(world: World): void {
    this.layout = world.layout;
    for (const e of world.events) {
      switch (e.t) {
        case 'landed': this.landed(e.typeId); break;
        case 'goaround': this.goaround(world.planes.find(p => p.id === e.id)?.type.id ?? 'medium'); break;
        case 'crash': this.crash(); break;
        case 'cleared': this.cleared(); break;
        // 'spawn' and 'nearmiss' stay silent by design — tension is the minor second under the drone, not a sound on top
        default: break;
      }
    }
    if (!this.g) return;
    this.setSky(airborneCount(world), world.warnPairs.size > 0);
    const tick = this.cfg.tick;
    for (const p of world.planes) {
      if (p.gone || p.rollout > 0) continue;
      let s = this.planes.get(p);
      if (!s) {
        s = { travel: tick * 0.5, lastDeg: -1, nextCall: world.elapsed + 1.2 };
        this.planes.set(p, s);
      } else if (p.idleSince < SIM_DT * 1.5) {
        // a command landed just before this step: the phrase starts over
        s.travel = tick * 0.6;
        s.lastDeg = -1;
      }
      if (p.path.length > 0 || p.holding) {
        s.travel += p.speed * SIM_DT;
        if (s.travel >= tick) { s.travel -= tick; this.planeNote(p, s); }
      } else if (world.elapsed >= s.nextCall) {
        s.nextCall = world.elapsed + this.cfg.calls * 4 * this.beatDur;
        this.call(p);
      }
    }
  }

  /** The finger, while a route is being drawn: a filtered breath that follows
   *  its speed, a faint tone that follows the ring under it, and a plink when
   *  the stroke crosses into a new ring (at most one per grid step). */
  inkMove(x: number, y: number, speedPxPerSec: number): void {
    const g = this.g;
    if (!g) return;
    try {
      const t = this.now;
      g.inkGain.gain.setTargetAtTime(Math.min(0.045, 0.004 + speedPxPerSec / 9000), t, 0.03);
      g.inkBP.frequency.setTargetAtTime(500 + Math.min(3000, speedPxPerSec * 3), t, 0.04);
      const deg = this.fieldDeg(x, y);
      g.prevOsc.frequency.setTargetAtTime(this.fieldFreq(deg), t, 0.05);
      g.prevGain.gain.setTargetAtTime(0.022, t, 0.05);
      if (deg !== this.inkDeg) {
        this.inkDeg = deg;
        if (t > this.inkNoteAt) {
          this.inkNoteAt = t + this.grid * 0.9;
          this.voice('light', this.fieldFreq(deg), this.gridTime(t), 0.22, g.inkBus);
        }
      }
    } catch { /* no-op */ }
  }

  /** The finger lifts. A stroke accepted on a runway or a pad plays the tonic once: the line is accepted. */
  inkEnd(result: InkResult): void {
    const g = this.g;
    if (!g) return;
    try {
      const t = this.now;
      g.inkGain.gain.setTargetAtTime(0, t, 0.08);
      g.prevGain.gain.setTargetAtTime(0, t, 0.12);
      this.inkDeg = -1;
      if (result === 'runway' || result === 'pad') this.voice('medium', this.fieldFreq(0), this.gridTime(t), 0.3, g.inkBus);
    } catch { /* no-op */ }
  }

  // ---------- time and harmony ----------

  private get now(): number { return this.ctx ? this.ctx.currentTime : 0; }
  /** tempo breath: ±3% over four minutes, only while the conductor is running */
  private get effBpm(): number {
    return this.cfg.journey === 'off' ? this.cfg.bpm : this.cfg.bpm * (1 + 0.03 * Math.sin(TAU * this.now / 240));
  }
  private get beatDur(): number { return 60 / this.effBpm; }
  private get grid(): number { return this.beatDur / 2; }
  private get prog(): readonly Chord[] { return PROGS[this.active.padProg]; }
  private get chord(): Chord { const p = this.prog; return p[this.chordIdx % p.length]; }
  /** the root actually sounding, after any harmonic drift */
  private effRoot(): number { return this.cfg.root + this.driftOffset; }

  private fieldDeg(x: number, y: number): number {
    const d = this.layout ? nearestThreshold(this.layout, x, y).d : 0;
    return fieldDegree(d, this.cfg.ring);
  }
  private fieldFreq(deg: number): number { return mtof(this.effRoot() + degreeSemis(this.scale, deg)); }

  /** the conductor's state has nothing left to say: a fresh city, or the journey switched off */
  private resetArc(): void {
    const c = this.cfg;
    this.cond = null;
    this.mul = NO_MUL;
    this.active = { droneReg: c.droneReg, padVoicing: c.padVoicing, padProg: c.padProg };
    this.driftOffset = 0;
    this.driftIsAway = false;
    this.driftedMode = c.mode;
    this.scale = SCALES[this.driftedMode];
    this.phasesSinceDrift = 0;
    this.driftPending = false;
    this.motifStep = 0;
  }

  // ---------- the scheduler: look 150 ms ahead, stay a little ahead of the audio clock ----------

  private schedule(): void {
    if (!this.ctx || !this.g) return;
    try {
      const now = this.now;
      this.tickConductor(now);
      // the clock ran on without us (a throttled tab): pick the beat up from here, no catch-up burst
      if (this.nextBeat < now - 1) this.nextBeat = now + 0.05;
      const ahead = now + 0.15;
      while (this.nextBeat < ahead) {
        this.onBeat(this.nextBeat, this.beatIdx);
        this.beatIdx++;
        this.nextBeat += this.beatDur;
      }
    } catch { /* no-op */ }
  }

  /** the next eighth-note at or after `t` — everything struck lands on one */
  private gridTime(t: number): number {
    t = Math.max(t, this.now + 0.02);
    const k = Math.ceil((t - this.nextBeat) / this.grid);
    return this.nextBeat + k * this.grid;
  }

  private onBeat(t: number, i: number): void {
    const g = this.g!, c = this.cfg;
    if (c.pulse > 0) this.thump(t, [1, 0.45, 0.7, 0.45][i % 4] * c.pulse);
    const cb = c.chordBars * 4, pos = i % cb;
    if (i > 0 && pos === 0) this.nextChord();
    // the drone rests for the last part of each chord and returns with the next one
    if (c.droneRest > 0 && pos === Math.round(cb * (1 - c.droneRest))) {
      this.droneResting = true;
      g.droneGain.gain.setTargetAtTime(0, t, 2.0);
    } else if (pos === 0 && this.droneResting) {
      this.droneResting = false;
      g.droneGain.gain.setTargetAtTime(c.drone * this.mul.drone, t, 2.5);
    }
    if (this.rng.next() < c.sparks * this.mul.sparks) { // a spark somewhere high in the sky
      const deg = 7 + Math.floor(this.rng.next() * 6), at = t + (this.rng.next() < 0.5 ? 0 : this.grid);
      this.voice('jet', this.fieldFreq(deg), at, 0.2, g.bedBus);
    }
    this.motifBeat(t, i);
  }

  /** a slow arpeggio of the current chord, one note every two beats, only in phases marked motif on */
  private motifBeat(t: number, i: number): void {
    if (i % 2 !== 0) return;
    const c = this.cfg;
    const phaseWants = c.journey !== 'off' && this.cond !== null && this.cond.phase.motif;
    if (!phaseWants || c.motif <= 0) return;
    const slot = this.motifStep % 4;
    this.motifStep++;
    if (slot === 3) return; // one rest in four
    const deg = this.chord.d[this.motifPattern[slot]];
    this.voice('light', this.fieldFreq(deg), t, 0.18 * c.motif, this.g!.bedBus);
  }

  /** the resting heartbeat */
  private thump(t: number, s: number): void {
    const ctx = this.ctx!, g = this.g!;
    const o = ctx.createOscillator(), e = ctx.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime(58, t);
    o.frequency.exponentialRampToValueAtTime(44, t + 0.12);
    e.gain.setValueAtTime(0, t);
    e.gain.linearRampToValueAtTime(0.13 * s, t + 0.01);
    e.gain.exponentialRampToValueAtTime(0.0001, t + 0.2);
    o.connect(e);
    e.connect(g.bedBus);
    o.start(t);
    o.stop(t + 0.25);
    o.onended = () => { o.disconnect(); e.disconnect(); };
  }

  // ---------- the conductor ----------
  // Runs on the audio clock (checked every schedule() tick), not on traffic,
  // so the bed keeps evolving over an empty sky. Journey off never calls any
  // of this. Nothing here happens in under 20 s: continuous values glide,
  // discrete choices switch only at a chord change.

  private tickConductor(t: number): void {
    if (this.cfg.journey === 'off') { this.cond = null; return; }
    if (!this.cond) { this.beginPhase(0, t); return; }
    if (t >= this.cond.nextAt) {
      let idx = this.cond.idx, tries = 0;
      do { idx = (idx + 1) % PHASES.length; tries++; } while (tries < PHASES.length && this.rng.next() < 0.25); // ~25% of phases are skipped
      this.beginPhase(idx, t);
    }
  }

  /** Enter phase `idx`: jitter its multipliers and length, glide every continuous knob there
   *  over a quarter of the (jittered) phase, and count phases toward the next drift. */
  private beginPhase(idx: number, t: number): void {
    const ph = PHASES[idx % PHASES.length], gentle = this.cfg.journey === 'gentle';
    const mul = { ...NO_MUL };
    for (const k of Object.keys(ph.mul) as (keyof PhaseMul)[]) {
      let v = ph.mul[k] * (0.85 + this.rng.next() * 0.3);
      if (gentle) v = 1 + (v - 1) * 0.5;
      mul[k] = v;
    }
    const len = Math.max(1, this.cfg.phaseLen) * (0.8 + this.rng.next() * 0.45);
    this.cond = { idx, phase: ph, nextAt: t + len };
    this.mul = mul;
    if (this.g) this.applyContinuous(t, len / 4);
    this.phasesSinceDrift++;
    const every = ({ never: 0, '3': 3, '2': 2, '1': 1 } as const)[this.cfg.driftEvery];
    if (every && this.phasesSinceDrift >= every) { this.driftPending = true; this.phasesSinceDrift = 0; }
  }

  /** A phase's discrete picks (register, voicing, cycle), a pending drift and a fresh motif
   *  pattern all land here — never mid-phrase, only when a chord actually turns over. */
  private nextChord(): void {
    const c = this.cfg;
    this.active = (c.journey === 'full' && this.cond)
      ? { droneReg: this.cond.phase.register, padVoicing: this.cond.phase.voicing, padProg: this.cond.phase.cycle }
      : { droneReg: c.droneReg, padVoicing: c.padVoicing, padProg: c.padProg };
    if (this.driftPending) { this.driftPending = false; this.driftNow(); }
    this.motifPattern = MOTIF_PATTERNS[Math.floor(this.rng.next() * MOTIF_PATTERNS.length)];
    this.motifStep = 0;
    this.applyChord((this.chordIdx + 1) % this.prog.length, false);
  }

  /** Move the key: home ↔ away, so the offset never grows unbounded (see `driftAway`). */
  private driftNow(): void {
    const c = this.cfg;
    if (this.driftIsAway) {
      this.driftOffset = 0;
      this.driftedMode = c.mode;
      this.driftIsAway = false;
    } else {
      const away = driftAway(c.mode, c.drift, this.rng.next() < 0.5 ? 1 : -1);
      if (!away) return;
      this.driftedMode = away.mode;
      this.driftOffset = away.offset;
      this.driftIsAway = true;
    }
    this.scale = SCALES[this.driftedMode];
  }

  // ---------- one struck note in an aircraft's voice ----------

  private voice(typeId: string, freq: number, t: number, vel: number, dest?: AudioNode): void {
    const ctx = this.ctx, g = this.g;
    if (!ctx || !g) return;
    const spec = VOICES[typeId] ?? VOICES.medium;
    const env = gainNode(ctx, 1);
    let head: AudioNode = env;
    env.connect(dest ?? g.planeBus);
    if (spec.trem) {
      const trem = gainNode(ctx, 0.55), lfo = ctx.createOscillator(), lg = gainNode(ctx, 0.45);
      lfo.frequency.value = spec.trem;
      lfo.connect(lg);
      lg.connect(trem.gain);
      lfo.start(t);
      lfo.stop(t + spec.dur + 0.3);
      trem.connect(env);
      head = trem;
      lfo.onended = () => { lfo.disconnect(); lg.disconnect(); };
    }
    const a = spec.attack ?? 0.004;
    let stopAt = t;
    for (const [ratio, amp, decay] of spec.partials) {
      const o = ctx.createOscillator(), e = ctx.createGain();
      o.type = spec.wave ?? 'sine';
      o.frequency.value = freq * ratio;
      e.gain.setValueAtTime(0, t);
      e.gain.linearRampToValueAtTime(amp * vel * 0.17, t + a);
      e.gain.exponentialRampToValueAtTime(0.0001, t + a + decay);
      e.gain.setValueAtTime(0, t + a + decay + 0.01);
      o.connect(e);
      e.connect(head);
      const end = t + a + decay + 0.05;
      o.start(t);
      o.stop(end);
      stopAt = Math.max(stopAt, end);
      o.onended = () => { o.disconnect(); e.disconnect(); };
    }
    const tail = head;
    setTimeout(() => { env.disconnect(); if (tail !== env) tail.disconnect(); }, Math.max(0, stopAt - this.now + 0.3) * 1000);
    this.notes++;
  }

  /** an aircraft crosses a tick on its line: play the ring it is nearest to (a repeat plays softer) */
  private planeNote(p: Aircraft, s: PlaneState): void {
    try {
      const deg = this.fieldDeg(p.x, p.y), vel = deg === s.lastDeg ? 0.4 : 0.75;
      s.lastDeg = deg;
      this.voice(p.type.id, this.fieldFreq(deg), this.gridTime(this.now), vel);
    } catch { /* no-op */ }
  }

  /** an aircraft with no line asks a question: its ring, then the ring above */
  private call(p: Aircraft): void {
    try {
      const deg = this.fieldDeg(p.x, p.y), t = this.gridTime(this.now);
      this.voice(p.type.id, this.fieldFreq(deg), t, 0.3);
      this.voice(p.type.id, this.fieldFreq(deg + 1), t + this.grid, 0.22);
    } catch { /* no-op */ }
  }

  /** the answer: home, with a fifth, and the pad takes a breath */
  private landed(typeId: string): void {
    const g = this.g;
    if (!g) return;
    try {
      const t = this.gridTime(this.now), f = this.fieldFreq(0), pad = this.cfg.pad * this.mul.pad, how = this.cfg.landing;
      if (how === 'chord tone') { // the pad's colour tone, so landings follow the harmony instead of always spelling the root
        const n = this.scale.length, d = this.chord.d.find(x => x % n !== 0) ?? 0;
        this.voice(typeId, this.fieldFreq(d < 0 ? d + n : d), t, 0.9);
      } else if (how === 'fifth') {
        this.voice(typeId, f * 1.5, t, 0.9);
      } else {
        this.voice(typeId, f, t, 0.9);
        if (how === 'tonic + fifth') this.voice(typeId, f * 1.5, t + 0.03, 0.3);
      }
      g.padGain.gain.setTargetAtTime(pad * 1.6, t, 0.3);
      g.padGain.gain.setTargetAtTime(pad, t + 1.5, 1.5);
    } catch { /* no-op */ }
  }

  /** two descending steps */
  private goaround(typeId: string): void {
    if (!this.g) return;
    try {
      const t = this.gridTime(this.now);
      this.voice(typeId, this.fieldFreq(2), t, 0.5);
      this.voice(typeId, this.fieldFreq(1), t + this.grid, 0.4);
    } catch { /* no-op */ }
  }

  /** The bed ducks to almost nothing with a low thud — no crash sound effect — and, since the
   *  level is over, fades out from there rather than recovering. */
  private crash(): void {
    this.bedWanted = false;
    const ctx = this.ctx, g = this.g;
    if (!ctx || !g) return;
    try {
      const t = this.now, b = g.bedBus.gain;
      b.cancelScheduledValues(t);
      b.setTargetAtTime(0.12, t, 0.25);
      b.setTargetAtTime(0, t + 2.5, 2.0);
      const o = ctx.createOscillator(), e = ctx.createGain();
      o.type = 'sine';
      o.frequency.value = 40;
      e.gain.setValueAtTime(0, t);
      e.gain.linearRampToValueAtTime(0.28, t + 0.02);
      e.gain.exponentialRampToValueAtTime(0.0001, t + 0.8);
      o.connect(e);
      e.connect(g.master);
      o.start(t);
      o.stop(t + 0.9);
      o.onended = () => { o.disconnect(); e.disconnect(); };
    } catch { /* no-op */ }
  }

  /** Skies cleared: a small rising phrase up the chord, on the grid. The bed keeps playing under
   *  the clear veil — the sky is still there — and retunes if the next city is chosen. */
  private cleared(): void {
    if (!this.g) return;
    try {
      const t = this.gridTime(this.now);
      [0, 2, 4].forEach((deg, i) => this.voice('medium', this.fieldFreq(deg), t + i * this.grid, i === 2 ? 0.8 : 0.6));
    } catch { /* no-op */ }
  }

  /** the sky's state: wind opens with traffic, a minor second creeps in under the drone when two get close */
  private setSky(airborne: number, warn: boolean): void {
    const g = this.g;
    if (!g) return;
    try {
      const t = this.now;
      if (airborne !== this.airborne) {
        this.airborne = airborne;
        this.applyWind(t, 1.5);
        g.windBP.frequency.setTargetAtTime(480 + 110 * airborne, t, 2);
      }
      if (warn !== this.warn) {
        this.warn = warn;
        g.tenseGain.gain.setTargetAtTime(warn ? 0.05 : 0, t, warn ? 0.6 : 0.4);
      }
    } catch { /* no-op */ }
  }

  // ---------- the graph ----------

  private buildGraph(ctx: AudioContext): Graph {
    const c = this.cfg;
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -20; comp.knee.value = 14; comp.ratio.value = 3; comp.attack.value = 0.01; comp.release.value = 0.35;
    const master = gainNode(ctx, this.muted ? 0 : MASTER_LEVEL);
    master.connect(comp);
    comp.connect(ctx.destination);
    const verb = ctx.createConvolver();
    verb.buffer = makeIR(ctx, c.space);
    this.irSeconds = c.space;
    const verbGain = gainNode(ctx, c.verb * this.mul.verb);
    verb.connect(verbGain);
    verbGain.connect(master);
    const planeBus = gainNode(ctx, 1);
    const planeLP = ctx.createBiquadFilter();
    planeLP.type = 'lowpass'; planeLP.frequency.value = c.bright * this.mul.bright; planeLP.Q.value = 0.3;
    planeBus.connect(planeLP);
    planeLP.connect(master);
    planeLP.connect(verb);
    // the bed starts silent: bedIn() raises it once a level is live
    const bedBus = gainNode(ctx, 0);
    bedBus.connect(master);
    const bedSend = gainNode(ctx, 0.5);
    bedBus.connect(bedSend);
    bedSend.connect(verb);
    const inkBus = gainNode(ctx, 1);
    inkBus.connect(master);
    inkBus.connect(verb);
    const noise = makeNoise(ctx, 2.5);

    // ground: two barely detuned sines under the root, through a lowpass (colour), a slow
    // swell (a multiplier that breathes over ~28 s) and the level gain, which also does the rests
    const droneGain = gainNode(ctx, 0);
    const droneLP = ctx.createBiquadFilter();
    droneLP.type = 'lowpass'; droneLP.frequency.value = c.droneCut;
    const droneSwell = gainNode(ctx, 1);
    droneLP.connect(droneSwell);
    droneSwell.connect(droneGain);
    droneGain.connect(bedBus);
    const droneLFO = ctx.createOscillator();
    droneLFO.frequency.value = 0.035;
    const droneLFOG = gainNode(ctx, 0);
    droneLFO.connect(droneLFOG);
    droneLFOG.connect(droneSwell.gain);
    droneLFO.start();
    const droneOsc = [-1, 1].map(sgn => {
      const o = ctx.createOscillator();
      o.type = 'sine'; o.frequency.value = 55; o.detune.value = sgn * c.droneShimmer;
      o.connect(droneLP);
      o.start();
      return o;
    });
    // pad: the level node stays; the three chord voices are built per instrument by buildPad()
    const padGain = gainNode(ctx, 0);
    padGain.connect(bedBus);
    const padLFO = ctx.createOscillator();
    padLFO.frequency.value = 0.045;
    const padLFOG = gainNode(ctx, 480 * c.padMove * this.mul.move);
    padLFO.connect(padLFOG);
    padLFO.start();
    const pad = this.buildPad(ctx, c.padType, noise, padLFOG, padGain);
    pad.mix.gain.value = 1;
    // wind: pink noise through a bandpass that opens with traffic, swelling on its own slow clock
    const windGain = gainNode(ctx, 0);
    const windBP = ctx.createBiquadFilter();
    windBP.type = 'bandpass'; windBP.frequency.value = 600; windBP.Q.value = 0.5;
    const wsrc = ctx.createBufferSource();
    wsrc.buffer = noise; wsrc.loop = true;
    wsrc.connect(windBP);
    windBP.connect(windGain);
    windGain.connect(bedBus);
    wsrc.start();
    const windLFO = ctx.createOscillator();
    windLFO.frequency.value = 0.06;
    const wl = gainNode(ctx, 0.005);
    windLFO.connect(wl);
    wl.connect(windGain.gain);
    windLFO.start();
    // tension: a minor second above the drone, silent until two aircraft get close
    const tenseGain = gainNode(ctx, 0);
    const tenseOsc = ctx.createOscillator();
    tenseOsc.type = 'sine'; tenseOsc.frequency.value = mtof(this.effRoot() - 23);
    tenseOsc.connect(tenseGain);
    tenseGain.connect(bedBus);
    tenseOsc.start();
    // the finger: filtered breath that follows speed, a faint tone that follows the ring under it
    const isrc = ctx.createBufferSource();
    isrc.buffer = noise; isrc.loop = true;
    const inkBP = ctx.createBiquadFilter();
    inkBP.type = 'bandpass'; inkBP.frequency.value = 900; inkBP.Q.value = 1.4;
    const inkGain = gainNode(ctx, 0);
    isrc.connect(inkBP);
    inkBP.connect(inkGain);
    inkGain.connect(inkBus);
    isrc.start();
    const prevOsc = ctx.createOscillator();
    prevOsc.type = 'sine'; prevOsc.frequency.value = 440;
    const prevGain = gainNode(ctx, 0);
    prevOsc.connect(prevGain);
    prevGain.connect(inkBus);
    prevOsc.start();

    return {
      master, verb, verbGain, planeBus, planeLP, bedBus, inkBus, noise,
      droneGain, droneLP, droneSwell, droneLFOG, droneOsc,
      padGain, padLFOG, pad,
      windGain, windBP, tenseGain, tenseOsc,
      inkGain, inkBP, prevOsc, prevGain,
    };
  }

  private buildPad(ctx: AudioContext, type: PadVoice, noise: AudioBuffer, padLFOG: GainNode, padGain: GainNode): PadInstrument {
    const spec = PADS[type], mix = gainNode(ctx, 0);
    const sources: AudioScheduledSourceNode[] = [], filters: BiquadFilterNode[] = [];
    const drawbarDepths: { node: GainNode; amp: number }[] = [], filterDrift2s: GainNode[] = [], qDrifts: GainNode[] = [];
    mix.connect(padGain);
    let noiseSrc: AudioBufferSourceNode | null = null;
    const idxs = DRAWBAR_IDX[type];
    const voices = [0, 1, 2].map(() => {
      const tuned: { p: AudioParam; ratio: number }[] = [], partialGains: GainNode[] = [];
      const out = gainNode(ctx, spec.level);
      out.connect(mix);
      let head: AudioNode = out, lp: BiquadFilterNode | null = null;
      if (spec.cut) {
        lp = ctx.createBiquadFilter();
        lp.type = 'lowpass'; lp.frequency.value = spec.cut; lp.Q.value = 0.7;
        padLFOG.connect(lp.frequency);
        lp.connect(head);
        head = lp;
        filters.push(lp);
      }
      if (spec.trem) {
        const tg = gainNode(ctx, 1), lfo = ctx.createOscillator(), lg = gainNode(ctx, 0.15);
        lfo.frequency.value = spec.trem;
        lfo.connect(lg);
        lg.connect(tg.gain);
        lfo.start();
        tg.connect(head);
        head = tg;
        sources.push(lfo);
      }
      for (const [ratio, amp, detune, wave] of spec.partials) {
        const g = gainNode(ctx, amp);
        g.connect(head);
        partialGains.push(g);
        if (wave === 'noise') {
          if (!noiseSrc) {
            noiseSrc = ctx.createBufferSource();
            noiseSrc.buffer = noise; noiseSrc.loop = true;
            noiseSrc.start();
            sources.push(noiseSrc);
          }
          const bp = ctx.createBiquadFilter();
          bp.type = 'bandpass'; bp.Q.value = 30;
          noiseSrc.connect(bp);
          bp.connect(g);
          tuned.push({ p: bp.frequency, ratio });
          if (type === 'breath') { // the bandpass Q wanders 20–40 on a 120 s period, its own phase per tone
            const lfo = ctx.createOscillator(), dg = gainNode(ctx, 0);
            lfo.frequency.value = 1 / 120;
            lfo.connect(dg);
            dg.connect(bp.Q);
            lfo.start(ctx.currentTime + this.rng.next() * 120);
            sources.push(lfo);
            qDrifts.push(dg);
          }
        } else {
          const o = ctx.createOscillator();
          o.type = wave; o.detune.value = detune;
          o.connect(g);
          o.start();
          sources.push(o);
          tuned.push({ p: o.frequency, ratio });
        }
      }
      if (idxs) idxs.forEach((pi, k) => { // organ/glass: the upper drawbars breathe, each on its own prime-second period
        const period = DRAWBAR_PERIODS[k], lfo = ctx.createOscillator(), dg = gainNode(ctx, 0);
        lfo.frequency.value = 1 / period;
        lfo.connect(dg);
        dg.connect(partialGains[pi].gain);
        lfo.start(ctx.currentTime + this.rng.next() * period);
        sources.push(lfo);
        drawbarDepths.push({ node: dg, amp: spec.partials[pi][1] });
      });
      if ((type === 'warm' || type === 'strings') && lp) { // a second, slower filter LFO, half the depth of the primary sweep
        const lfo = ctx.createOscillator(), dg = gainNode(ctx, 0);
        lfo.frequency.value = 1 / 180;
        lfo.connect(dg);
        dg.connect(lp.frequency);
        lfo.start(ctx.currentTime + this.rng.next() * 180);
        sources.push(lfo);
        filterDrift2s.push(dg);
      }
      return { tuned };
    });
    const pad: PadInstrument = { type, mix, voices, sources, filters, drawbarDepths, filterDrift2s, qDrifts };
    this.applyDrawbarDepths(pad, ctx.currentTime, 0.01); // an initial, non-zero depth without a startup glide
    return pad;
  }

  /** The pad's timbre drifts too, so it is never the same twice: the organ/glass upper drawbars
   *  breathe, warm/strings get a second slow filter sweep, breath's bandpass Q wanders. All at zero
   *  depth (so no audible or numeric change) while the journey is off. */
  private applyDrawbarDepths(pad: PadInstrument, t: number, tau: number): void {
    const journeyOn = this.cfg.journey !== 'off';
    const depthScale = journeyOn ? this.cfg.padMove * this.mul.move : 0;
    for (const d of pad.drawbarDepths) d.node.gain.setTargetAtTime(d.amp * depthScale * 0.5, t, tau);
    for (const f of pad.filterDrift2s) f.gain.setTargetAtTime(240 * depthScale, t, tau);
    for (const q of pad.qDrifts) q.gain.setTargetAtTime(journeyOn ? 10 : 0, t, tau);
  }

  /** swap the pad's instrument with a crossfade; the old one is stopped once it is inaudible */
  private setPadType(type: PadVoice): void {
    const ctx = this.ctx, g = this.g;
    if (!ctx || !g || g.pad.type === type) return;
    const old = g.pad, t = this.now;
    g.pad = this.buildPad(ctx, type, g.noise, g.padLFOG, g.padGain);
    this.applyChord(this.chordIdx, true);
    g.pad.mix.gain.setTargetAtTime(1, t, 0.8);
    old.mix.gain.setTargetAtTime(0, t, 0.8);
    setTimeout(() => {
      for (const n of old.sources) { try { n.stop(); } catch { /* already stopped */ } }
      for (const f of old.filters) { try { g.padLFOG.disconnect(f.frequency); } catch { /* not connected */ } }
      old.mix.disconnect();
    }, 4000);
  }

  /** The drone's pitch: root (or a fifth below it) in the chosen register, moving with the pad chord's bass if asked. */
  private droneMidi(): number {
    const c = this.cfg, move = c.droneMove === 'follows the chord' ? degreeSemis(this.scale, this.chord.d[0]) : 0;
    return this.effRoot() + REG[this.active.droneReg] + move;
  }

  private applyDrone(t: number, immediate: boolean): void {
    const g = this.g;
    if (!g) return;
    const c = this.cfg, f = Math.max(40, mtof(this.droneMidi())); // a drift never lands the drone below 40 Hz
    g.droneOsc.forEach((o, i) => {
      if (immediate) o.frequency.value = f; else o.frequency.setTargetAtTime(f, t, 1.2);
      o.detune.setTargetAtTime((i ? 1 : -1) * c.droneShimmer, t, 0.5);
    });
    g.droneLP.frequency.setTargetAtTime(c.droneCut, t, 0.6);
    g.droneSwell.gain.setTargetAtTime(1 - c.droneSwell * 0.5, t, 0.8);
    g.droneLFOG.gain.setTargetAtTime(c.droneSwell * 0.5, t, 0.8);
  }

  /** Apply the city's settings to a live graph: everything glides, nothing restarts. Called on
   *  `setLevel`. Never writes into `cfg`: continuous knobs go through the phase multiplier and
   *  discrete ones through `active`, so a tuned value still means what it says while a phase
   *  is scaling it. */
  private applyAll(): void {
    const ctx = this.ctx, g = this.g;
    if (!ctx || !g) return;
    try {
      const t = this.now, c = this.cfg;
      g.tenseOsc.frequency.setTargetAtTime(mtof(this.effRoot() - 23), t, 1.2);
      this.applyContinuous(t, 0.8);
      this.applyDrone(t, false);
      this.setPadType(c.padType);
      this.applyChord(this.chordIdx, false);
      if (Math.abs(c.space - this.irSeconds) > 0.05) { // a new room: rebuilt off the hot path
        if (this.irTimer) clearTimeout(this.irTimer);
        this.irTimer = setTimeout(() => {
          this.irSeconds = c.space;
          try { g.verb.buffer = makeIR(ctx, c.space); } catch { /* no-op */ }
        }, 250);
      }
    } catch { /* no-op */ }
  }

  /** Retarget every continuous knob the conductor can scale, to `tuned × phase multiplier`,
   *  gliding over `tau` seconds: 0.8 s on a city change, `phaseLen / 4` on a phase boundary. */
  private applyContinuous(t: number, tau: number): void {
    const g = this.g;
    if (!g) return;
    const c = this.cfg, m = this.mul;
    if (!this.droneResting) g.droneGain.gain.setTargetAtTime(c.drone * m.drone, t, tau);
    g.padGain.gain.setTargetAtTime(c.pad * m.pad, t, tau);
    g.verbGain.gain.setTargetAtTime(c.verb * m.verb, t, tau);
    g.planeLP.frequency.setTargetAtTime(c.bright * m.bright, t, tau);
    g.padLFOG.gain.setTargetAtTime(480 * c.padMove * m.move, t, tau);
    this.applyWind(t, tau);
    this.applyDrawbarDepths(g.pad, t, tau);
  }

  private applyWind(t: number, tau: number): void {
    const g = this.g;
    if (!g) return;
    const airborne = Math.max(0, this.airborne);
    g.windGain.gain.setTargetAtTime((0.006 + 0.004 * airborne) * this.cfg.wind * this.mul.wind, t, tau);
  }

  private applyChord(idx: number, immediate: boolean): void {
    this.chordIdx = idx;
    const g = this.g;
    if (!g) return;
    const t = this.now, root = this.effRoot(), off = VOICING[this.active.padVoicing];
    this.chord.d.forEach((deg, v) => {
      const f = mtof(root + off[v] + degreeSemis(this.scale, deg));
      for (const { p, ratio } of g.pad.voices[v].tuned) {
        if (immediate) p.value = f * ratio; else p.setTargetAtTime(f * ratio, t, this.cfg.padGlide);
      }
    });
    this.applyDrone(t, immediate);
  }

  private rampBed(target: number, seconds: number): void {
    const g = this.g;
    if (!g) return;
    try {
      const now = this.now, b = g.bedBus.gain;
      b.cancelScheduledValues(now);
      b.setValueAtTime(b.value, now);
      b.linearRampToValueAtTime(target, now + seconds);
    } catch { /* no-op */ }
  }

  private applyMasterGain(): void {
    const g = this.g;
    if (!g) return;
    try {
      const now = this.now, m = g.master.gain;
      m.cancelScheduledValues(now);
      m.setValueAtTime(m.value, now);
      m.linearRampToValueAtTime(this.muted ? 0 : MASTER_LEVEL, now + 0.05); // quick, click-free
    } catch { /* no-op */ }
  }
}
